import { CallExpressionShape, Plugin, unloadBuilder, type OutputFile, type Record } from '@servicenow/sdk-build-core'
import { NowIdShape, NowIncludeShape } from '@servicenow/sdk-build-plugins'
import {
    computeDataAccessValue,
    computeSecurityAclValue,
    computeTeamValue,
    computeVersionDetailsValue,
    mapTriggerConfigs,
} from './ai-agentic-workflow-transform-helper'
import {
    createDataAccessConfigRecord,
    createTeamMemberRecords,
    createTeamRecord,
    createTriggerConfigRecord,
    createTriggerMappingRecord,
    createUseCaseConfigRecord,
    createVersionRecords,
    createWorkflowRecord,
    extractPublishedInstructions,
    validateTriggerConfig,
} from './ai-agentic-workflow-shapes-helper'
import {
    GEN_AI_AGENTIC_USECASE_TYPE,
    SN_AIA_TEAM,
    SN_AIA_TEAM_MEMBER,
    SN_AIA_TRIGGER_CONFIG,
    SN_AIA_TRIGGER_MAPPING,
    SN_AIA_USECASE,
    SN_AIA_USECASE_CONFIG,
    SN_AIA_VERSION,
    SYS_AGENT_ACCESS_ROLE_CONFIG,
    SYS_AGENT_ACCESS_ROLE_MAPPING,
    SYS_SECURITY_ACL,
    SYS_SECURITY_ACL_ROLE,
    SYS_USER_ROLE,
} from './constants'
import { processSecurityAcl } from './security-acl-processor'

export const AiAgenticWorkflowPlugin = Plugin.create({
    name: 'AiAgenticWorkflowPlugin',
    docs: [
        {
            apiName: 'AiAgenticWorkflow',
            tags: ['sn_aia_agentic_workflow'],
        },
    ],
    records: {
        [SN_AIA_USECASE]: {
            relationships: {
                [SN_AIA_TEAM]: {
                    via: 'team',
                    inverse: true,
                    descendant: true,
                    relationships: {
                        [SN_AIA_TEAM_MEMBER]: {
                            via: 'team',
                            descendant: true,
                        },
                    },
                },
                [SN_AIA_USECASE_CONFIG]: {
                    via: 'usecase_configuration',
                    descendant: true,
                },
                [SN_AIA_TRIGGER_CONFIG]: {
                    via: 'usecase',
                    descendant: true,
                    relationships: {
                        [SN_AIA_TRIGGER_MAPPING]: {
                            via: 'trigger_configuration',
                            descendant: true,
                        },
                    },
                },
                [SN_AIA_VERSION]: {
                    via: 'target_id',
                    descendant: true,
                },
                [SYS_AGENT_ACCESS_ROLE_CONFIG]: {
                    via: 'agent',
                    descendant: true,
                    relationships: {
                        [SYS_AGENT_ACCESS_ROLE_MAPPING]: {
                            via: 'agent_access_config',
                            descendant: true,
                            relationships: {
                                [SYS_USER_ROLE]: {
                                    via: 'role',
                                    inverse: true,
                                },
                            },
                        },
                    },
                },
                [SYS_SECURITY_ACL]: {
                    via: [{ parent: ['internal_name', '\\.(.*)'], child: ['name', '\\.(.*)'] }],
                    descendant: true,
                    relationships: {
                        [SYS_SECURITY_ACL_ROLE]: {
                            via: 'sys_security_acl',
                            descendant: true,
                        },
                    },
                },
            },
            async toShape(record, { descendants, database, transform }) {
                // Process script fields with NowIncludeShape only if script has content
                const scriptValue = record.get('context_processing_script')?.ifString()?.ifNotEmpty()
                const contextProcessingScript = scriptValue
                    ? await NowIncludeShape.fromRecord(record, record.get('context_processing_script'), transform)
                    : undefined

                // Query descendants
                const teams = descendants.query(SN_AIA_TEAM)
                const teamRecord = teams.length > 0 ? teams[0] : undefined
                const teammembers = descendants.query(SN_AIA_TEAM_MEMBER)
                const triggerConfigs = descendants.query(SN_AIA_TRIGGER_CONFIG)
                const triggerMappings = descendants.query(SN_AIA_TRIGGER_MAPPING)
                const versions = descendants.query(SN_AIA_VERSION)
                const useCaseConfigs = descendants.query(SN_AIA_USECASE_CONFIG)
                const useCaseConfig = useCaseConfigs.length > 0 ? useCaseConfigs[0] : undefined
                const allRoleConfigs = descendants.query(SYS_AGENT_ACCESS_ROLE_CONFIG)
                const roleMappings = descendants.query(SYS_AGENT_ACCESS_ROLE_MAPPING)

                const dataAccessConfig = allRoleConfigs.find(
                    (config) => config.get('agent_table')?.getValue() === SN_AIA_USECASE
                )

                // Get workflow's internal_name to match against ACL
                const internalName = record.get('internal_name').ifString()?.getValue() || ''
                const matchingAcl = database.get(SYS_SECURITY_ACL, {
                    name: internalName,
                    type: GEN_AI_AGENTIC_USECASE_TYPE,
                })

                // If the ACL was auto-generated via securityAcl, reconstruct that config.
                const matchingAclId = matchingAcl?.getId().getValue()
                const aclRoleRecords = matchingAclId
                    ? descendants.query(SYS_SECURITY_ACL_ROLE).filter((r) => {
                          const aclRef = r.get('sys_security_acl')
                          const refId = aclRef?.isRecord() ? aclRef.getId().getValue() : aclRef?.ifString()?.getValue()
                          return refId === matchingAclId
                      })
                    : []
                const securityAclValue = computeSecurityAclValue(matchingAcl, aclRoleRecords)

                // Compute values BEFORE transform using helper functions
                const teamValue = computeTeamValue(teamRecord, teammembers)
                const triggerConfigValue = mapTriggerConfigs(triggerConfigs, triggerMappings)
                const versionsValue = computeVersionDetailsValue(versions)
                const dataAccessValue = computeDataAccessValue(dataAccessConfig, roleMappings)
                const runAsValue = useCaseConfig?.get('run_as_user')?.ifString()?.ifNotEmpty()?.getValue()
                // Find the first non-default memory_scope across all team members
                // Since memoryScope is a workflow-level setting applied to all members,
                // prefer any non-'global' value (the meaningful one)
                const memoryScopeValue =
                    teammembers.length > 0
                        ? (
                              teammembers.find(
                                  (tm) =>
                                      tm.get('memory_scope')?.ifString()?.getValue() !== undefined &&
                                      tm.get('memory_scope')?.ifString()?.getValue() !== 'global'
                              ) ?? teammembers[0]
                          )
                              ?.get('memory_scope')
                              ?.ifString()
                              ?.getValue()
                        : undefined

                return {
                    success: true,
                    value: new CallExpressionShape({
                        source: record,
                        callee: 'AiAgenticWorkflow',
                        args: [
                            record.transform(({ $ }) => ({
                                $id: $.val(NowIdShape.from(record) ?? record.getId().getValue()),
                                name: $.from('name'),
                                description: $.from('description'),
                                contextProcessingScript: contextProcessingScript
                                    ? $.val(contextProcessingScript)
                                    : $.from('context_processing_script').def(''),
                                sysDomain: $.from('sys_domain').def('global'),
                                active: $.from('active').toBoolean().def(true),
                                recordType: $.from('record_type').def('template'),
                                executionMode: $.from('execution_mode')
                                    .map((v) => v || 'copilot')
                                    .def('copilot'),
                                securityAcl: $.val(securityAclValue),
                                runAs: $.val(runAsValue).def(''),
                                dataAccess: $.val(dataAccessValue),
                                team: $.val(teamValue),
                                ...(triggerConfigs.length > 0 ? { triggerConfig: $.val(triggerConfigValue) } : {}),
                                memoryScope: $.val(memoryScopeValue).def('global'),
                                versions: $.val(versionsValue),
                            })),
                        ],
                    }),
                }
            },
            async toFile(record, context) {
                const { config, transform, descendants, database } = context

                // Check for DELETE-action sys_security_acl_role records scoped to this workflow's ACLs
                // (descendants only has INSERT_OR_UPDATE, so DELETE records are missed)
                const workflowAclIds = new Set(descendants.query(SYS_SECURITY_ACL).map((r) => r.getId().getValue()))
                const deleteAclRoles = database.query(SYS_SECURITY_ACL_ROLE).filter((rec) => {
                    if (rec.getAction() !== 'DELETE') {
                        return false
                    }
                    const aclRef = rec.get('sys_security_acl')?.toString().getValue()
                    return aclRef ? workflowAclIds.has(aclRef) : false
                })

                // No DELETE records — use default serialization
                if (deleteAclRoles.length === 0) {
                    return { success: false }
                }

                // Custom serialization to include DELETE XMLs
                const recordBuilder = unloadBuilder(config)
                const updateName = await transform.getUpdateName(record)
                const builder = recordBuilder.record(record, updateName)

                record
                    .entries()
                    .sort(([a], [b]) => a.localeCompare(b))
                    .forEach(([prop, shape]) => {
                        builder.field(prop, shape)
                    })

                // Helper to serialize a descendant record
                const serializeDescendant = async (descendantRecord: Record) => {
                    const descUpdateName = await transform.getUpdateName(descendantRecord)
                    const descBuilder = unloadBuilder(config)
                    const descRecordBuilder = descBuilder.record(descendantRecord, descUpdateName)

                    descendantRecord
                        .entries()
                        .sort(([a], [b]) => a.localeCompare(b))
                        .forEach(([prop, shape]) => {
                            descRecordBuilder.field(prop, shape)
                        })

                    return {
                        source: descendantRecord,
                        name: `${descUpdateName}.xml`,
                        category: descendantRecord.getInstallCategory(),
                        content: descBuilder.end(),
                    }
                }

                const descendantFiles: OutputFile[] = []

                // Serialize all standard descendant tables
                for (const table of [
                    SN_AIA_TEAM,
                    SN_AIA_TEAM_MEMBER,
                    SN_AIA_USECASE_CONFIG,
                    SN_AIA_TRIGGER_CONFIG,
                    SN_AIA_TRIGGER_MAPPING,
                    SN_AIA_VERSION,
                    SYS_AGENT_ACCESS_ROLE_CONFIG,
                    SYS_AGENT_ACCESS_ROLE_MAPPING,
                    SYS_SECURITY_ACL,
                    SYS_SECURITY_ACL_ROLE,
                ]) {
                    for (const rec of descendants.query(table)) {
                        descendantFiles.push(await serializeDescendant(rec))
                    }
                }

                // Emit DELETE XMLs for deleted sys_security_acl_role records
                for (const rec of deleteAclRoles) {
                    descendantFiles.push(await serializeDescendant(rec))
                }

                return {
                    success: true,
                    value: [
                        {
                            source: record,
                            name: `${updateName}.xml`,
                            category: record.getInstallCategory(),
                            content: recordBuilder.end(),
                        },
                        ...descendantFiles,
                    ],
                }
            },
        },
        [SN_AIA_TEAM]: {
            coalesce: ['name'],
        },
        [SN_AIA_TEAM_MEMBER]: {
            coalesce: ['agent', 'team'],
        },
        [SN_AIA_VERSION]: {
            coalesce: ['target_id', 'version_name'],
        },
        [SN_AIA_TRIGGER_MAPPING]: {
            coalesce: ['trigger_configuration', 'related_resource_record'],
        },
        [SN_AIA_TRIGGER_CONFIG]: {
            coalesce: ['name', 'usecase'],
        },
        [SN_AIA_USECASE_CONFIG]: {
            coalesce: ['usecase_configuration'],
        },
        [SYS_AGENT_ACCESS_ROLE_CONFIG]: {
            coalesce: ['agent'],
        },
        [SYS_AGENT_ACCESS_ROLE_MAPPING]: {
            coalesce: ['agent_access_config', 'role'],
        },
        [SYS_SECURITY_ACL_ROLE]: {
            coalesce: ['sys_security_acl', 'sys_user_role'],
        },
    },
    shapes: [
        {
            shape: CallExpressionShape,
            fileTypes: ['fluent'],
            async toRecord(callExpression, { factory, diagnostics, config }) {
                // Validate that this is an AiAgenticWorkflow call
                if (callExpression.getCallee() !== 'AiAgenticWorkflow') {
                    return { success: false }
                }

                const arg = callExpression.getArgument(0).asObject()

                // Compute internal_name for workflow
                const workflowName = arg.get('name').ifString()?.getValue() || ''
                const sysDomain = arg.get('sysDomain')?.ifString()?.getValue() || 'global'
                const scopeName = config.scope
                const computedWorkflowInternalName = `${sysDomain}.${scopeName}.${workflowName}`

                // Extract published instructions from versions
                const aiVersions = arg.get('versions')
                const {
                    instructions: publishedInstructions,
                    error: versionError,
                    errorSource,
                } = extractPublishedInstructions(aiVersions, workflowName)
                if (versionError) {
                    // Use errorSource (specific state field) if available, otherwise fall back to aiVersions array
                    diagnostics.error(errorSource || aiVersions, versionError)
                    return { success: false }
                }

                // Validate securityAcl (mandatory field)
                const securityAclField = arg.get('securityAcl')?.ifObject()
                if (!securityAclField) {
                    diagnostics.error(arg, 'AI Agentic Workflow must have a securityAcl field.')
                    return { success: false }
                }

                // Validate dataAccess
                const dataAccess = arg.get('dataAccess')?.ifObject()
                const runAsUser = arg.get('runAs')

                // Create team record (if provided)
                const teamObj = arg.get('team')
                let teamRecord: Record | undefined
                const teamMembers = teamObj?.isObject() ? teamObj.get('members') : undefined

                if (teamObj?.isObject()) {
                    const workflowDescription = arg.get('description')
                    teamRecord = await createTeamRecord(
                        factory,
                        callExpression,
                        teamObj,
                        SN_AIA_TEAM,
                        workflowDescription
                    )
                }

                // If securityAcl is provided, auto-generate the ACL + role records.
                let securityAclRecords: Awaited<ReturnType<typeof processSecurityAcl>> | undefined
                if (securityAclField) {
                    securityAclRecords = await processSecurityAcl(
                        securityAclField,
                        computedWorkflowInternalName,
                        factory,
                        diagnostics
                    )
                }

                // Create main workflow record
                const workflowRecord = await createWorkflowRecord(
                    factory,
                    callExpression,
                    computedWorkflowInternalName,
                    publishedInstructions,
                    teamRecord?.getId(),
                    SN_AIA_USECASE,
                    securityAclRecords?.aclRecord.getId().getValue()
                )

                // Create team member records
                const workflowMemoryScope = arg.get('memoryScope')?.ifString()?.getValue() || 'global'
                const workflowDomain = arg.get('sysDomain')?.ifString()?.getValue() || 'global'
                const teamMemberRecords =
                    teamMembers && teamRecord
                        ? await createTeamMemberRecords(
                              factory,
                              callExpression,
                              teamMembers,
                              teamRecord.getId(),
                              workflowRecord.getId(),
                              workflowMemoryScope,
                              workflowDomain,
                              SN_AIA_TEAM_MEMBER,
                              diagnostics
                          )
                        : []

                // Create version records
                const aiVersionRecords = await createVersionRecords(
                    factory,
                    callExpression,
                    workflowRecord.getId(),
                    workflowRecord.get('sys_domain'),
                    aiVersions,
                    SN_AIA_VERSION
                )

                // Handle triggerConfig array
                const triggerConfigs = arg.get('triggerConfig')?.ifArray()?.getElements() ?? []
                const triggerConfigRecords: Record[] = []

                for (const triggerConfig of triggerConfigs) {
                    if (triggerConfig?.isObject()) {
                        // Validate trigger configuration
                        const validationResult = validateTriggerConfig(triggerConfig)
                        if (validationResult) {
                            diagnostics.error(validationResult.errorSource, validationResult.error)
                            return { success: false }
                        }

                        // Compute internal_name for trigger
                        const triggerName = triggerConfig.get('name')?.ifString()?.getValue() || ''
                        const triggerDomain = triggerConfig.get('domain')?.ifString()?.getValue() || 'global'
                        const computedTriggerInternalName = `${triggerDomain}.${scopeName}.${triggerName}`

                        const triggerConfigRecord = await createTriggerConfigRecord(
                            factory,
                            callExpression,
                            triggerConfig,
                            computedTriggerInternalName,
                            workflowRecord.getId(),
                            SN_AIA_TRIGGER_CONFIG,
                            diagnostics
                        )
                        triggerConfigRecords.push(triggerConfigRecord)
                    }
                }

                // Always create useCaseConfigRecord
                const useCaseConfigRecord = await createUseCaseConfigRecord(
                    factory,
                    callExpression,
                    workflowRecord.getId(),
                    SN_AIA_USECASE_CONFIG
                )

                // Auto-generate triggerMapping records
                const triggerMappingRecords: Record[] = []
                for (let i = 0; i < triggerConfigRecords.length; i++) {
                    const linkedTriggerConfig = triggerConfigRecords[i]
                    const correspondingTriggerConfig = triggerConfigs[i]

                    if (linkedTriggerConfig && correspondingTriggerConfig?.isObject()) {
                        const triggerMappingRecord = await createTriggerMappingRecord(
                            factory,
                            callExpression,
                            workflowRecord.getId(),
                            correspondingTriggerConfig,
                            linkedTriggerConfig.getId(),
                            SN_AIA_TRIGGER_MAPPING
                        )
                        triggerMappingRecords.push(triggerMappingRecord)
                    }
                }

                // Create data access configuration record + M2M mapping rows
                let dataAccessRecords: Awaited<ReturnType<typeof createDataAccessConfigRecord>> | undefined
                if (dataAccess) {
                    const runAsUserValue = runAsUser?.ifString()?.getValue()
                    dataAccessRecords = await createDataAccessConfigRecord(
                        factory,
                        callExpression,
                        workflowRecord.getId(),
                        dataAccess,
                        SYS_AGENT_ACCESS_ROLE_CONFIG,
                        diagnostics,
                        !!runAsUserValue
                    )
                }

                // Collect all child records
                const childRecords = [
                    teamRecord,
                    ...teamMemberRecords,
                    ...aiVersionRecords,
                    useCaseConfigRecord,
                    ...triggerConfigRecords,
                    ...triggerMappingRecords,
                ]
                if (dataAccessRecords) {
                    childRecords.push(...dataAccessRecords)
                }
                // Add auto-generated ACL + role records when securityAcl was used
                if (securityAclRecords) {
                    childRecords.push(...securityAclRecords.allRecords)
                }

                return {
                    success: true,
                    value: workflowRecord.with(...childRecords.filter((r): r is Record => r !== undefined)),
                }
            },
        },
    ],
})
